import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '../components/ui/alert-dialog';
import { jobsAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
    Plus, 
    Search, 
    FileText,
    ChevronRight,
    Clock,
    CheckCircle2,
    Send,
    MapPin,
    Trash2
} from 'lucide-react';

const statusColors = {
    Draft: 'bg-slate-100 text-slate-700 border-slate-200',
    ReadyToSend: 'bg-amber-100 text-amber-700 border-amber-200',
    Sent: 'bg-blue-100 text-blue-700 border-blue-200',
    Accepted: 'bg-green-100 text-green-700 border-green-200',
    Completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    Lost: 'bg-red-100 text-red-700 border-red-200'
};

const StatusIcon = ({ status }) => {
    switch (status) {
        case 'Draft':
            return <FileText className="h-4 w-4" />;
        case 'Sent':
            return <Send className="h-4 w-4" />;
        case 'Accepted':
        case 'Completed':
            return <CheckCircle2 className="h-4 w-4" />;
        default:
            return <Clock className="h-4 w-4" />;
    }
};

export const Jobs = () => {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        fetchJobs();
    }, [statusFilter]);

    const fetchJobs = async (search = '') => {
        try {
            const params = { search };
            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }
            const response = await jobsAPI.list(params);
            setJobs(response.data);
        } catch (error) {
            console.error('Failed to fetch jobs:', error);
            toast.error('Failed to load jobs');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteJob = async (jobId, e) => {
        e?.stopPropagation();
        try {
            await jobsAPI.delete(jobId);
            setJobs(prev => prev.filter(j => j.id !== jobId));
            toast.success('Job deleted');
        } catch {
            toast.error('Failed to delete job');
        }
    };

    const handleSearch = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        const timeoutId = setTimeout(() => {
            fetchJobs(query);
        }, 300);
        return () => clearTimeout(timeoutId);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0
        }).format(value || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    };

    const getAddressString = (address) => {
        if (!address) return '';
        const parts = [address.line1, address.city, address.postcode].filter(Boolean);
        return parts.join(', ');
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-4">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-12 w-full" />
                    {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} className="h-24 w-full rounded-lg" />
                    ))}
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-heading text-2xl lg:text-3xl font-bold uppercase tracking-tight text-slate-900">
                            Jobs
                        </h1>
                        <p className="text-slate-500 text-sm mt-1">{jobs.length} total</p>
                    </div>
                    <Link to="/jobs/new">
                        <Button 
                            className="h-12 px-4 bg-red-600 hover:bg-red-700 text-white font-semibold uppercase tracking-wide shadow-sm"
                            data-testid="new-job-btn"
                        >
                            <Plus className="h-5 w-5 mr-2" />
                            <span className="hidden sm:inline">New Job</span>
                        </Button>
                    </Link>
                </div>

                {/* Search */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                        type="search"
                        placeholder="Search by quote number or customer..."
                        value={searchQuery}
                        onChange={handleSearch}
                        className="h-12 pl-10 border-slate-300"
                        data-testid="job-search"
                    />
                </div>

                {/* Status Tabs */}
                <Tabs value={statusFilter} onValueChange={setStatusFilter}>
                    <TabsList className="w-full h-auto p-1 bg-slate-100 grid grid-cols-5 gap-1">
                        <TabsTrigger 
                            value="all" 
                            className="text-xs py-2 data-[state=active]:bg-white"
                            data-testid="filter-all"
                        >
                            All
                        </TabsTrigger>
                        <TabsTrigger 
                            value="Draft" 
                            className="text-xs py-2 data-[state=active]:bg-white"
                            data-testid="filter-draft"
                        >
                            Draft
                        </TabsTrigger>
                        <TabsTrigger 
                            value="Sent" 
                            className="text-xs py-2 data-[state=active]:bg-white"
                            data-testid="filter-sent"
                        >
                            Sent
                        </TabsTrigger>
                        <TabsTrigger 
                            value="Accepted" 
                            className="text-xs py-2 data-[state=active]:bg-white"
                            data-testid="filter-accepted"
                        >
                            Won
                        </TabsTrigger>
                        <TabsTrigger 
                            value="Completed" 
                            className="text-xs py-2 data-[state=active]:bg-white"
                            data-testid="filter-completed"
                        >
                            Done
                        </TabsTrigger>
                    </TabsList>
                </Tabs>

                {/* Jobs List */}
                {jobs.length > 0 ? (
                    <div className="space-y-2">
                        {jobs.map((job) => (
                            <div key={job.id} className="relative group" data-testid={`job-card-${job.quote_number}`}>
                                <Link to={`/jobs/${job.id}`} className="block">
                                    <Card className="bg-white border-slate-200 shadow-sm hover:border-red-200 hover:shadow-md transition-all">
                                        <CardContent className="p-4">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex items-start gap-3 min-w-0 flex-1">
                                                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${statusColors[job.status] || 'bg-slate-100'}`}>
                                                        <StatusIcon status={job.status} />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <p className="font-semibold text-slate-900">
                                                                {job.quote_number}
                                                            </p>
                                                            <Badge className={`${statusColors[job.status]} text-xs border`}>
                                                                {job.status}
                                                            </Badge>
                                                        </div>
                                                        <p className="text-sm font-medium text-slate-700 truncate">
                                                            {job.customer_name}
                                                        </p>
                                                        {job.site_address && (
                                                            <p className="text-xs text-slate-500 truncate flex items-center gap-1 mt-1">
                                                                <MapPin className="h-3 w-3" />
                                                                {getAddressString(job.site_address)}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-right flex-shrink-0">
                                                    <p className="font-bold text-slate-900 text-lg">
                                                        {formatCurrency(job.pricing_summary?.total)}
                                                    </p>
                                                    <p className="text-xs text-slate-500">
                                                        {formatDate(job.created_at)}
                                                    </p>
                                                </div>
                                                <ChevronRight className="h-5 w-5 text-slate-400 flex-shrink-0 self-center" />
                                            </div>
                                        </CardContent>
                                    </Card>
                                </Link>
                                {/* Delete button — sits outside the Link to avoid navigation */}
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <button
                                            onClick={(e) => e.stopPropagation()}
                                            data-testid={`delete-job-${job.quote_number}`}
                                            className="absolute top-3 right-10 w-7 h-7 flex items-center justify-center rounded-md text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                                            title="Delete job"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Delete {job.quote_number}?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                This will permanently delete the job for <strong>{job.customer_name}</strong>. This cannot be undone.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction
                                                onClick={(e) => handleDeleteJob(job.id, e)}
                                                className="bg-red-600 hover:bg-red-700"
                                            >
                                                Delete
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        ))}
                    </div>
                ) : (
                    <Card className="bg-white border-slate-200">
                        <CardContent className="p-8 text-center">
                            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FileText className="h-8 w-8 text-slate-400" />
                            </div>
                            <p className="text-slate-600 font-medium">No jobs found</p>
                            <p className="text-slate-500 text-sm mt-1 mb-4">
                                {searchQuery || statusFilter !== 'all' 
                                    ? 'Try adjusting your filters' 
                                    : 'Start your first survey'}
                            </p>
                            {!searchQuery && statusFilter === 'all' && (
                                <Link to="/jobs/new">
                                    <Button className="bg-red-600 hover:bg-red-700 text-white" data-testid="empty-new-job-btn">
                                        <Plus className="h-4 w-4 mr-2" />
                                        Create Job
                                    </Button>
                                </Link>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
};
